import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router";
import {
  CheckCircle2,
  Cloud,
  Edit2,
  Loader2,
  Plus,
  RefreshCw,
  Search,
  Trash2,
  XCircle,
  Zap,
} from "lucide-react";
import {
  searchCloudProviders,
  updateCloudProvider,
  deleteCloudProvider,
  testCloudConnection,
  createCloudAccount,
  CloudProviderResponse,
  listOrgUnits,
  OrgUnit,
} from "../../api/frameworksApi";
import { useDiscoveryStore } from "../../stores/discoveryStore";
import { useToastStore } from "../../stores/toastStore";
import { RBACAction, RBACMultiAction } from "../components/RBACButton";
import {
  Modal,
  FormField,
  FormInput,
  FormSelect,
  ModalFooterButtons,
} from "../components/Modal";
import { DataTable, Column } from "../components/DataTable";
import { TableToolbar } from "../components/TableToolbar";
import { DeleteConfirmDialog } from "../components/DeleteConfirmDialog";
import { Tip } from "../components/ui/tooltip";
import { flattenOrgHierarchy } from "./cmdb/cmdbUtils";

type CloudProvider = "AWS" | "AZURE" | "GCP" | "OCI";
type CloudRole = "PRODUCTION" | "DEVELOPMENT" | "STAGING" | "DR" | "UAT" | "SANDBOX";
type CloudStatus = "ACTIVE" | "INACTIVE" | "SUSPENDED";
type CredentialType = "ACCESS_KEY" | "ASSUME_ROLE";

interface CreateForm {
  orgId: string;
  provider: CloudProvider;
  credentialType: CredentialType;
  // ACCESS_KEY fields
  accessKey: string;
  secretKey: string;
  // ASSUME_ROLE fields
  roleArn: string;
  externalId: string;
  // Optional
  accountName: string;
  role: CloudRole;
  description: string;
  status: CloudStatus;
}

interface EditForm {
  accountName: string;
  role: CloudRole;
  status: CloudStatus;
  description: string;
}

const defaultCreateForm: CreateForm = {
  orgId: "",
  provider: "AWS",
  credentialType: "ACCESS_KEY",
  accessKey: "",
  secretKey: "",
  roleArn: "",
  externalId: "",
  accountName: "",
  role: "PRODUCTION",
  description: "",
  status: "ACTIVE",
};

const PROVIDER_COLORS: Record<CloudProvider, string> = {
  AWS: "bg-amber-100 text-amber-700 dark:bg-amber-500/20 dark:text-amber-400",
  AZURE: "bg-blue-100 text-blue-700 dark:bg-blue-500/20 dark:text-blue-400",
  GCP: "bg-emerald-100 text-emerald-700 dark:bg-emerald-500/20 dark:text-emerald-400",
  OCI: "bg-red-100 text-red-700 dark:bg-red-500/20 dark:text-red-400",
};

const STATUS_COLORS: Record<CloudStatus, string> = {
  ACTIVE: "bg-emerald-100 text-emerald-700 dark:bg-emerald-500/20 dark:text-emerald-400",
  INACTIVE: "bg-gray-100 text-gray-700 dark:bg-gray-500/20 dark:text-gray-400",
  SUSPENDED: "bg-red-100 text-red-700 dark:bg-red-500/20 dark:text-red-400",
};

/** Credential fields that reset test state when changed */
const CREDENTIAL_FIELDS: (keyof CreateForm)[] = [
  "orgId", "provider", "credentialType",
  "accessKey", "secretKey", "roleArn", "externalId",
];

/**
 * Sortable column key -> backend field name sent as `sortByColumn` in
 * POST /cmdb/ci/cloud/{page}/{size}. Sorting is fully server-side; a key with
 * no entry here is forwarded as-is.
 */
const CLOUD_SORT_COLUMNS: Record<string, string> = {
  accountName: "accountName",
  provider: "provider",
  // The column shows how many regions the account spans.
  regions: "regionCount",
  role: "role",
  status: "status",
};

export function CMDBCloudAccounts() {
  const navigate = useNavigate();
  /* ── List state ──
   * Search, sorting and pagination are all resolved by the backend via
   * POST /cmdb/ci/cloud/{page}/{size}; `accounts` always holds exactly the
   * rows for the current page and is rendered as-is. */
  const [accounts, setAccounts] = useState<CloudProviderResponse[]>([]);
  const [orgOptions, setOrgOptions] = useState<OrgUnit[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  // The keyword actually sent to the backend (committed on Enter / icon click).
  const [keyword, setKeyword] = useState("");
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [sortKey, setSortKey] = useState<string | null>(null);
  const [sortDir, setSortDir] = useState<"asc" | "desc">("asc");

  // ── Modal state ──
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState<CloudProviderResponse | null>(null);
  const [createForm, setCreateForm] = useState<CreateForm>(defaultCreateForm);
  const [editForm, setEditForm] = useState<EditForm>({
    accountName: "", role: "PRODUCTION", status: "ACTIVE", description: "",
  });
  const [saving, setSaving] = useState(false);

  // ── Test Connection state ──
  const [testStatus, setTestStatus] = useState<"idle" | "testing" | "success" | "failed">("idle");
  const [testError, setTestError] = useState("");
  const [testedAccountId, setTestedAccountId] = useState("");

  // ── Discovery — delegated to global store ──
  const { runs: discoveryRuns, startDiscovery } = useDiscoveryStore();

  // Re-fetch accounts list whenever any discovery run completes successfully
  const prevRunsRef = useRef<typeof discoveryRuns>({});
  useEffect(() => {
    const prev = prevRunsRef.current;
    const hasNewSuccess = Object.values(discoveryRuns).some(
      (run) => run.status === "SUCCESS" && prev[run.cloudProviderId]?.status !== "SUCCESS"
    );
    if (hasNewSuccess) loadAccounts();
    prevRunsRef.current = discoveryRuns;
  }, [discoveryRuns]);

  // ── Delete ──
  const [deletingAccount, setDeletingAccount] = useState<CloudProviderResponse | null>(null);
  const [deleting, setDeleting] = useState(false);

  // The skeleton is only shown before the very first page ever lands; every
  // later fetch keeps the current rows visible (no blank page on paging).
  const firstLoadDoneRef = useRef(false);
  const loadAccounts = useCallback(async () => {
    if (!firstLoadDoneRef.current) setLoading(true);
    try {
      const res = await searchCloudProviders(page, pageSize, {
        keyword: keyword.trim(),
        // Unsorted (the initial state) sends both sort fields as empty.
        sortByColumn: sortKey ? (CLOUD_SORT_COLUMNS[sortKey] ?? sortKey) : "",
        sortingOrder: sortKey ? sortDir : "",
      });
      setAccounts(res.items);
      setTotal(res.totalItems);
      // The current page can fall off the end of the result set (a delete, a
      // larger page size, a narrower search) — step back to the last real page.
      if (res.items.length === 0 && page > 0 && res.totalItems > 0) {
        setPage(Math.max(0, Math.ceil(res.totalItems / pageSize) - 1));
      }
    } catch (err) {
      useToastStore.getState().showError("Error",
        err instanceof Error ? err.message : "Failed to load cloud accounts");
      setAccounts([]);
      setTotal(0);
    } finally {
      firstLoadDoneRef.current = true;
      setLoading(false);
    }
  }, [page, pageSize, keyword, sortKey, sortDir]);

  useEffect(() => {
    loadAccounts();
  }, [loadAccounts]);

  useEffect(() => {
    loadOrgOptions();
  }, []);

  /* ── Search submit ──
   * Typing alone never hits the API: the keyword is only pushed to the
   * backend on Enter / the search button, or the moment the box is cleared. */
  const submitSearch = (value: string) => {
    const next = value.trim();
    if (next === keyword) return;
    setKeyword(next);
    setPage(0);
  };

  const loadOrgOptions = async () => {
    try {
      const res = await listOrgUnits();
      setOrgOptions(flattenOrgHierarchy(res.data || [], ["ORGANIZATION"]));
    } catch {
      /* silent */
    }
  };

  // ── Update a create-form field; reset test if credential field changes ──
  const updateCreateField = <K extends keyof CreateForm>(key: K, value: CreateForm[K]) => {
    setCreateForm((prev) => ({ ...prev, [key]: value }));
    if (CREDENTIAL_FIELDS.includes(key) && testStatus !== "idle") {
      setTestStatus("idle");
      setTestedAccountId("");
      setTestError("");
    }
  };

  // ── Open create modal ──
  const openCreate = () => {
    setEditing(null);
    setCreateForm(defaultCreateForm);
    setTestStatus("idle");
    setTestedAccountId("");
    setTestError("");
    setShowModal(true);
  };

  // ── Open edit modal ──
  const openEdit = (account: CloudProviderResponse) => {
    setEditing(account);
    setEditForm({
      accountName: account.accountName,
      role: account.role,
      status: account.status,
      description: account.description || "",
    });
    setShowModal(true);
  };

  const closeModal = () => {
    if (saving || testStatus === "testing") return;
    setShowModal(false);
    setEditing(null);
  };

  // ── Test Connection ──
  const handleTestConnection = async () => {
    if (!createForm.orgId) {
      useToastStore.getState().showError("Error", "Select an Org Unit first");
      return;
    }
    if (!createForm.accessKey.trim() || !createForm.secretKey.trim()) {
      useToastStore.getState().showError("Error", "Access Key and Secret Key are required");
      return;
    }

    setTestStatus("testing");
    setTestError("");
    setTestedAccountId("");
    try {
      const res = await testCloudConnection({
        orgId: createForm.orgId,
        provider: createForm.provider,
        credentialType: createForm.credentialType,
        accessKey: createForm.accessKey,
        secretKey: createForm.secretKey,
      });
      if (res.data?.success) {
        setTestStatus("success");
        setTestedAccountId(res.data.accountId);
      } else {
        setTestStatus("failed");
        setTestError(res.data?.message || "Connection failed");
      }
    } catch (err) {
      setTestStatus("failed");
      setTestError(err instanceof Error ? err.message : "Connection test failed");
    }
  };

  // ── Save (create) ──
  const handleCreate = async () => {
    if (saving || testStatus !== "success") return;
    if (!createForm.accountName.trim()) {
      useToastStore.getState().showError("Validation", "Account Name is required");
      return;
    }
    setSaving(true);
    try {
      await createCloudAccount({
        orgId: createForm.orgId,
        provider: createForm.provider,
        credentialType: createForm.credentialType,
        accessKey: createForm.accessKey,
        secretKey: createForm.secretKey,
        accountName: createForm.accountName,
        role: createForm.role,
        description: createForm.description || undefined,
        status: createForm.status,
      });
      useToastStore.getState().showSuccess(
        "Account created",
        "Click ⚡ Discover to scan regions and resources"
      );
      setShowModal(false);
      loadAccounts();
    } catch (err) {
      useToastStore.getState().showError("Error",
        err instanceof Error ? err.message : "Failed to create cloud account");
    } finally {
      setSaving(false);
    }
  };

  // ── Save (edit) ──
  const handleEdit = async () => {
    if (!editing || saving) return;
    setSaving(true);
    try {
      await updateCloudProvider(editing.id, {
        accountName: editForm.accountName,
        role: editForm.role,
        status: editForm.status,
        description: editForm.description || undefined,
      });
      useToastStore.getState().showSuccess("Success", "Cloud account updated");
      setShowModal(false);
      loadAccounts();
    } catch (err) {
      useToastStore.getState().showError("Error",
        err instanceof Error ? err.message : "Failed to update cloud account");
    } finally {
      setSaving(false);
    }
  };

  // ── Discover — delegated to global store ──
  const handleDiscover = useCallback((row: CloudProviderResponse) => {
    startDiscovery(row.id, row.accountName);
  }, [startDiscovery]);

  // ── Delete ──
  const confirmDelete = async () => {
    if (!deletingAccount) return;
    setDeleting(true);
    try {
      await deleteCloudProvider(deletingAccount.id);
      useToastStore.getState().showSuccess("Success", "Cloud account deleted");
      setDeletingAccount(null);
      loadAccounts();
    } catch (err) {
      useToastStore.getState().showError("Error",
        err instanceof Error ? err.message : "Delete failed");
    } finally {
      setDeleting(false);
    }
  };

  /* ── Rows for the current page ──
   * No client-side filtering, sorting or slicing: the backend already applied
   * the keyword, sort column/order and page window. */
  const paginated = accounts;

  const SORTABLE_KEYS = new Set([
    "accountName",
    "provider",
    "regions",
    "role",
    "status",
  ]);

  const handleSort = (key: string) => {
    setPage(0);
    if (sortKey === key) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else {
      setSortKey(key);
      setSortDir("asc");
    }
  };

  const columns: Column<CloudProviderResponse>[] = [
    {
      key: "_sno",
      header: "#",
      width: "w-10",
      align: "center",
      render: (_, i) => <span className="text-sm d">{page * pageSize + i + 1}</span>,
    },
    {
      key: "accountName",
      header: "Account Name",
      align: "left",
      headerClassName: "!text-center",
      render: (row) => (
        <div className="flex items-left gap-2">
          <Cloud className="size-4 d flex-shrink-0" />
          <span className="font-medium">{row.accountName}</span>
        </div>
      ),
    },
    {
      key: "provider",
      header: "Provider",
      align: "center",
      render: (row) => (
        <span className={`px-2 py-0.5 rounded-md text-xs font-bold ${PROVIDER_COLORS[row.provider] || ""}`}>
          {row.provider}
        </span>
      ),
    },
    // {
    //   key: "accountId",
    //   header: "Account ID",
    //   align: "left",
    //   render: (row) => <span className="font-mono text-sm d">{row.accountId}</span>,
    // },
    {
      key: "regions",
      header: "Regions",
      align: "center",
      render: (row) => {
        const count = row.regions?.length ?? 0;
        if (count === 0) {
          return (
            <Tip label="Run discovery to detect regions" side="top">
              <span
                className="text-xs text-muted-foreground italic"
              >
                Not scanned yet
              </span>
            </Tip>
          );
        }
        return (
          <Tip label={row.regions!.join(", ")} side="top">
            <span
              className="text-sm d cursor-default"
            >
              {count} region{count !== 1 ? "s" : ""}
            </span>
          </Tip>
        );
      },
    },
    {
      key: "role",
      header: "Role",
      align: "left",
      headerClassName: "!text-center",
      render: (row) => (
        <span className="text-sm capitalize">
          {row.role?.toLowerCase().replace(/_/g, " ")}
        </span>
      ),
    },
    {
      key: "status",
      header: "Status",
      align: "center",
      render: (row) => (
        <span className={`px-2 py-1 rounded-md text-xs font-medium capitalize ${STATUS_COLORS[row.status] || ""}`}>
          {row.status?.toLowerCase().replace(/_/g, " ")}
        </span>
      ),
    },
    {
      key: "_actions",
      header: "Actions",
      align: "center",
      width: "w-28",
      render: (row) => (
        <div className="flex items-center justify-center gap-1.5">
          {/* Discover */}
          <RBACAction module="cmdb" action="update">
            <Tip label="Discover Resources" side="bottom">
              <button
                onClick={(e) => { e.stopPropagation(); handleDiscover(row); }}
                disabled={discoveryRuns[row.id]?.status === "RUNNING"}
                className="p-1.5 hover:bg-muted rounded transition-colors disabled:opacity-50"
              >
                {discoveryRuns[row.id]?.status === "RUNNING"
                  ? <Loader2 className="size-4 animate-spin text-indigo-500" />
                  : <Zap className="size-4 d hover:text-indigo-500" />}
              </button>
            </Tip>
          </RBACAction>
          {/* Edit */}
          <RBACAction module="cmdb" action="update">
            <Tip label="Edit Cloud Account" side="bottom">
              <button
                onClick={(e) => { e.stopPropagation(); openEdit(row); }}
                className="p-1.5 hover:bg-indigo-50 dark:hover:bg-indigo-500/10 rounded transition-colors"
              >
                <Edit2 className="size-4 d hover:text-indigo-600" />
              </button>
            </Tip>
          </RBACAction>
          {/* Delete */}
          <RBACAction module="cmdb" action="delete">
            <Tip label="Delete Cloud Account" side="bottom">
              <button
                onClick={(e) => { e.stopPropagation(); setDeletingAccount(row); }}
                className="p-1.5 hover:bg-muted rounded transition-colors"
              >
                <Trash2 className="size-4 d hover:text-red-600" />
              </button>
            </Tip>
          </RBACAction>
        </div>
      ),
    },
  ];

  const sortableColumns = columns.map((c) =>
    SORTABLE_KEYS.has(c.key) ? { ...c, sortable: true } : c,
  );

  const saveDisabled = editing ? saving : (saving || testStatus !== "success");

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Cloud Accounts</h1>
          <p className="text-sm d mt-0.5">Manage AWS, Azure, GCP, and OCI cloud provider accounts</p>
        </div>
        <div className="flex items-center gap-2 self-start sm:self-auto flex-shrink-0">
          <RBACMultiAction checks={[["cmdb", "create"], ["cmdb", "update"]]} type="any">
            <button
              onClick={openCreate}
              className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
            >
              <Plus className="size-4" />
              New Cloud Account
            </button>
          </RBACMultiAction>
        </div>
      </div>

      {/* Search + Rows per page */}
      <div className="flex items-center gap-4 flex-wrap">
        <TableToolbar
          search={{
            value: searchQuery,
            onChange: setSearchQuery,
            onSubmit: submitSearch,
            placeholder: "Search by name, region",
            ariaLabel: "Search cloud accounts",
          }}
          pageSize={pageSize}
          onPageSizeChange={(size) => {
            setPageSize(size);
            setPage(0);
          }}
          pageSizeLabel="Rows per page"
        />
      </div>

      <DataTable
        columns={sortableColumns}
        data={paginated}
        loading={loading}
        emptyText="No cloud accounts found"
        page={page}
        pageSize={pageSize}
        totalItems={total}
        onPageChange={setPage}
        sortKey={sortKey}
        sortDir={sortDir}
        onSort={handleSort}
        onRowClick={(row) => navigate(`/cmdb/cloud-accounts/${row.id}`)}
      />

      {/* ── Create / Edit Modal ── */}
      <Modal
        open={showModal}
        onClose={closeModal}
        title={editing ? "Edit Cloud Account" : "Add Cloud Account"}
        size="lg"
        footer={
          editing ? (
            <ModalFooterButtons
              onCancel={closeModal}
              onSubmit={handleEdit}
              submitLabel={saving ? "Saving…" : "Save"}
              submitDisabled={saving}
            />
          ) : (
            /* Custom footer for create — 3 buttons */
            <div className="flex items-center justify-between gap-3 w-full">
              {/* Test Connection */}
              <button
                onClick={handleTestConnection}
                disabled={testStatus === "testing"}
                className="flex items-center gap-2 px-4 py-2 border border-border rounded-lg text-sm font-medium hover:bg-muted transition-colors disabled:opacity-50"
              >
                {testStatus === "testing" ? (
                  <><Loader2 className="size-4 animate-spin" /> Testing…</>
                ) : (
                  <><RefreshCw className="size-4" /> Test Connection</>
                )}
              </button>
              <div className="flex items-center gap-3">
                <button
                  onClick={closeModal}
                  className="px-4 py-2 border border-border rounded-lg text-sm font-medium hover:bg-muted transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleCreate}
                  disabled={saveDisabled}
                  className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg text-sm font-medium hover:bg-indigo-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {saving ? <><Loader2 className="size-4 animate-spin" /> Saving…</> : "Save"}
                </button>
              </div>
            </div>
          )
        }
      >
        {editing ? (
          /* ── Edit form ── */
          <div className="space-y-3">
            <p className="text-sm font-bold tracking-wide text-foreground">Account Details</p>

            {/* Read-only: provider + account ID */}
            <div className="grid grid-cols-2 gap-3">
              <FormField label="Provider">
                <p className="text-sm d px-3 py-2 border border-border rounded-lg bg-muted/50">{editing.provider}</p>
              </FormField>
              <FormField label="Account ID">
                <p className="text-sm font-mono d px-3 py-2 border border-border rounded-lg bg-muted/50">{editing.accountId}</p>
              </FormField>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <FormField label="Account Name">
                <FormInput
                  value={editForm.accountName}
                  onChange={(v) => setEditForm({ ...editForm, accountName: v })}
                  placeholder="e.g. Production AWS"
                />
              </FormField>
              <FormField label="Role" required>
                <FormSelect
                  value={editForm.role}
                  onChange={(v) => setEditForm({ ...editForm, role: v as CloudRole })}
                  options={[
                    { value: "PRODUCTION", label: "Production" },
                    { value: "DEVELOPMENT", label: "Development" },
                    { value: "STAGING", label: "Staging" },
                    { value: "DR", label: "Disaster Recovery" },
                    { value: "UAT", label: "UAT" },
                    { value: "SANDBOX", label: "Sandbox" },
                  ]}
                />
              </FormField>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <FormField label="Status" required>
                <FormSelect
                  value={editForm.status}
                  onChange={(v) => setEditForm({ ...editForm, status: v as CloudStatus })}
                  options={[
                    { value: "ACTIVE", label: "Active" },
                    { value: "INACTIVE", label: "Inactive" },
                    { value: "SUSPENDED", label: "Suspended" },
                  ]}
                />
              </FormField>
              <FormField label="Description">
                <FormInput
                  value={editForm.description}
                  onChange={(v) => setEditForm({ ...editForm, description: v })}
                  placeholder="Optional"
                />
              </FormField>
            </div>
          </div>
        ) : (
          /* ── Create form ── */
          <div className="space-y-5">

            {/* Section: Account */}
            <div className="space-y-3">
              <p className="text-sm font-bold tracking-wide text-foreground">Account</p>

              <div className="grid grid-cols-2 gap-3">
                <FormField label="Owning Org Unit" required>
                  <FormSelect
                    value={createForm.orgId}
                    onChange={(v) => updateCreateField("orgId", v)}
                    options={[
                      { value: "", label: "Select org unit" },
                      ...orgOptions.map((o) => ({ value: o.id, label: o.name })),
                    ]}
                  />
                </FormField>
                <FormField label="Provider" required>
                  <FormSelect
                    value={createForm.provider}
                    onChange={(v) => updateCreateField("provider", v as CloudProvider)}
                    options={[
                      { value: "AWS", label: "Amazon Web Services (AWS)" },
                      { value: "AZURE", label: "Microsoft Azure" },
                      { value: "GCP", label: "Google Cloud Platform (GCP)" },
                      { value: "OCI", label: "Oracle Cloud Infrastructure (OCI)" },
                    ]}
                  />
                </FormField>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <FormField label="Account Name" required>
                  <FormInput
                    value={createForm.accountName}
                    onChange={(v) => setCreateForm((p) => ({ ...p, accountName: v }))}
                    placeholder="e.g. Production AWS"
                  />
                </FormField>
                <FormField label="Account ID" hint="Auto-filled after Test Connection">
                  <div className="flex items-center gap-2 px-3 py-2 border border-border rounded-lg bg-muted/40 min-h-[38px]">
                    {testStatus === "success" ? (
                      <>
                        <CheckCircle2 className="size-4 text-emerald-500 shrink-0" />
                        <span className="font-mono text-sm text-emerald-600 dark:text-emerald-400">
                          {testedAccountId}
                        </span>
                      </>
                    ) : (
                      <span className="text-sm text-muted-foreground">
                        Test connection to auto-fill
                      </span>
                    )}
                  </div>
                </FormField>
              </div>
            </div>

            {/* Section: Credentials */}
            <div className="space-y-3">
              <p className="text-sm font-bold tracking-wide text-foreground">Credentials</p>

              {/* Credential type toggle (label removed per request) */}
              <div className="flex gap-4">
                {(["ACCESS_KEY", "ASSUME_ROLE"] as CredentialType[]).map((ct) => (
                  <label key={ct} className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="credentialType"
                      value={ct}
                      checked={createForm.credentialType === ct}
                      onChange={() => updateCreateField("credentialType", ct)}
                      className="accent-indigo-600"
                    />
                    <span className="text-sm">
                      {ct === "ACCESS_KEY" ? "Access Credentials" : "Assume Role"}
                    </span>
                  </label>
                ))}
              </div>

              {/* Access Key + Secret Key appear once a credential type is selected */}
              {createForm.credentialType && (
                <div className="grid grid-cols-2 gap-3">
                  <FormField label="Access Key" required>
                    <FormInput
                      value={createForm.accessKey}
                      onChange={(v) => updateCreateField("accessKey", v)}
                      name="cloud-access-key"
                      autoComplete="off"
                    />
                  </FormField>
                  <FormField label="Secret Key" required>
                    <FormInput
                      type="password"
                      value={createForm.secretKey}
                      onChange={(v) => updateCreateField("secretKey", v)}
                      name="cloud-secret-key"
                      autoComplete="new-password"
                    />
                  </FormField>
                </div>
              )}


              {/* Test result feedback */}
              {testStatus === "success" && (
                <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-emerald-50 dark:bg-emerald-500/10 border border-emerald-200 dark:border-emerald-500/20 text-sm text-emerald-700 dark:text-emerald-400">
                  <CheckCircle2 className="size-4 shrink-0" />
                  Connected to account <span className="font-mono font-semibold ml-1">{testedAccountId}</span>
                </div>
              )}
              {testStatus === "failed" && (
                <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-red-50 dark:bg-red-500/10 border border-red-200 dark:border-red-500/20 text-sm text-red-700 dark:text-red-400">
                  <XCircle className="size-4 shrink-0" />
                  {testError || "Connection failed"}
                </div>
              )}
            </div>

            {/* Section: Configuration */}
            <div className="space-y-3">
              <p className="text-sm font-bold tracking-wide text-foreground">Configuration</p>
              <div className="grid grid-cols-2 gap-3">
                <FormField label="Role" required>
                  <FormSelect
                    value={createForm.role}
                    onChange={(v) => setCreateForm((p) => ({ ...p, role: v as CloudRole }))}
                    options={[
                      { value: "PRODUCTION", label: "Production" },
                      { value: "DEVELOPMENT", label: "Development" },
                      { value: "STAGING", label: "Staging" },
                      { value: "DR", label: "Disaster Recovery" },
                      { value: "UAT", label: "UAT" },
                      { value: "SANDBOX", label: "Sandbox" },
                    ]}
                  />
                </FormField>
                <FormField label="Status" required>
                  <FormSelect
                    value={createForm.status}
                    onChange={(v) => setCreateForm((p) => ({ ...p, status: v as CloudStatus }))}
                    options={[
                      { value: "ACTIVE", label: "Active" },
                      { value: "INACTIVE", label: "Inactive" },
                      { value: "SUSPENDED", label: "Suspended" },
                    ]}
                  />
                </FormField>
              </div>
              <FormField label="Description">
                <FormInput
                  value={createForm.description}
                  onChange={(v) => setCreateForm((p) => ({ ...p, description: v }))}
                  placeholder="Optional"
                />
              </FormField>
            </div>
          </div>
        )}
      </Modal>

      <DeleteConfirmDialog
        open={!!deletingAccount}
        type="Cloud Account"
        name={deletingAccount?.accountName ?? ""}
        deleting={deleting}
        showReason={false}
        onCancel={() => !deleting && setDeletingAccount(null)}
        onConfirm={confirmDelete}
      />
    </div>
  );
}
